import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { hasValidToken } from '../../services/auth/authGuards';
import { useAuth } from '../../hooks/useAuth';

interface PublicRouteProps {
  children: React.ReactNode;
  /** Rota para onde o usuário autenticado deve ser redirecionado */
  redirectTo?: string;
}

/**
 * PublicRoute
 * - Exibe o conteúdo público normalmente quando NÃO há token válido.
 * - Se houver token válido, redireciona para `redirectTo` (padrão: '/dashboard').
 * - Não avalia assinatura ou perfil nesta rota; isso é tratado dentro das páginas.
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({ children, redirectTo = '/dashboard' }) => {
  const location = useLocation();
  const { authState } = useAuth();

  // Evita flicker durante inicialização do estado de auth
  // Removido loading visual para não bloquear navegação
  if (authState.isLoading) {
    // Retorna null ou children dependendo da estratégia desejada.
    // Retornar children permite que a interface pública carregue imediatamente.
    // Se o usuário estiver logado, o useEffect/redirecionamento ocorrerá assim que authState resolver.
    return <>{children}</>;
  }

  // Redireciona se houver token válido
  if (hasValidToken()) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  // Conteúdo público normal
  return <>{children}</>;
};

export default PublicRoute;